import { EditOutlined } from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { Empty } from "components/Empty/Empty"
import { InputInline } from "components/Input/InputInline"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import { Tag } from "components/Tag/Tag"
import { useDisclosure } from "hooks/useDisclosure"
import { Escalation } from "interface/escalation"
import { ComponentProps, useEffect, useRef } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import {
	useEscalationDetails,
	useUpdateEscalationObservations,
} from "services/escalations"
import { useReadNotification } from "services/notifications"
import { usePatientDetails } from "services/patients"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import { GENERIC_ERROR_MESSAGE } from "utils/constants"
import { dateFormatter } from "utils/date"
import { CloseEscalation } from "./CloseEscalation"
import { DeleteEscalation } from "./DeleteEscalation"
import { EscalationActivityFeed } from "./EscalationActivityFeed"
import { EscalationAssigneeObservers } from "./EscalationAssigneeObservers"
import { EscalationLinkedScreenings } from "./EscalationLinkedScreenings"

export function EscalationDetail() {
	const { scopeId, userId: patientId, escalationId } = useParams()
	const navigate = useNavigate()
	const {
		data: patient,
		isFetched: isPatientFetched,
		isLoading: isPatientLoading,
		isError: isPatientError,
		error: patientError,
	} = usePatientDetails(patientId)
	const {
		data: escalation,
		isFetched: isEscalationFetched,
		isLoading: isEscalationLoading,
		isError: isEscalationError,
		error: escalationError,
	} = useEscalationDetails(escalationId)
	const isLoading = isPatientLoading || isEscalationLoading
	const isError = isPatientError || isEscalationError
	const error = patientError ?? escalationError
	const isNotFound =
		isPatientFetched && isEscalationFetched && (!patient || !escalation)
	const isReady =
		isPatientFetched &&
		isEscalationFetched &&
		Boolean(patient && escalation) &&
		!isError
	const isOpen = escalation?.status === "OPEN"
	const formattedDate = dateFormatter(escalation?.createdAt, {
		hasYear: false,
	})
	const handleNavigateToPatient = () =>
		navigate(`/${scopeId}/patients/${patientId}`)

	useMarkNotificationRead()

	return (
		<Spinner spinning={isLoading}>
			<div className="@container mx-auto flex h-full max-h-full flex-col gap-8 overflow-hidden bg-white">
				<div className="flex flex-wrap justify-between gap-6">
					<BackButton onClick={handleNavigateToPatient}>
						{patient?.fullName ?? "All patients"}
					</BackButton>

					{isReady && patient && escalation && (
						<div className="flex flex-wrap items-center gap-3">
							<DeleteEscalation
								escalation={escalation}
								onSuccess={handleNavigateToPatient}
							/>

							<EscalationAssigneeObservers escalation={escalation} />

							<CloseEscalation escalation={escalation} />
						</div>
					)}
				</div>

				<div className="size-full overflow-y-auto">
					{(isNotFound || isError) && (
						<Empty
							title="Unable to load escalation"
							description={error?.message ?? GENERIC_ERROR_MESSAGE}
						/>
					)}

					{isReady && patient && escalation && (
						<div>
							<div className="flex flex-col gap-1">
								<div className="flex items-center gap-3">
									<h1 className="font-headings text-3xl font-semibold">
										Clinical escalation
									</h1>
									<Tag variant="filled" color={isOpen ? "blue" : "default"}>
										{isOpen ? "Open" : "Closed"}
									</Tag>
								</div>
								<p className="text-navy-60">{formattedDate}</p>
							</div>

							<div className="grid grid-cols-1 gap-x-12 gap-y-8 @6xl:grid-cols-[470px_1fr]">
								<Observations escalation={escalation} />

								<EscalationLinkedScreenings
									escalation={escalation}
									patient={patient}
									className="@6xl:row-span-2"
								/>

								<EscalationActivityFeed
									escalation={escalation}
									className="@6xl:row-span-2"
								/>
							</div>
						</div>
					)}
				</div>
			</div>
		</Spinner>
	)
}

function Observations({
	className,
	escalation: { id, observations, createdBy, status },
	...props
}: ComponentProps<"div"> & {
	escalation: Escalation
}) {
	const editButtonRef = useRef<HTMLButtonElement>(null)
	const update = useUpdateEscalationObservations(id)
	const editing = useDisclosure({
		onOpen: update.reset,
		onClose: () => {
			update.reset()
			requestAnimationFrame(() => editButtonRef.current?.focus())
		},
	})
	const { data: { id: userId } = {} } = useUserRole()
	const isEditable =
		Boolean(userId) && userId === createdBy && status === "OPEN"

	return (
		<div {...props} className={cn("flex flex-col gap-2", className)}>
			<div className="flex justify-between gap-2">
				<h2 className="text-xl">Observations</h2>
				{isEditable && (
					<Button
						ref={editButtonRef}
						type="link"
						disabled={editing.isOpen}
						icon={<EditOutlined aria-hidden />}
						aria-label="Edit observations"
						onClick={editing.open}
					/>
				)}
			</div>

			<InputInline
				readOnly={!isEditable || !editing.isOpen}
				defaultValue={observations}
				ariaLabel="Add observations"
				isPending={update.isPending}
				placeholder="Add observations..."
				errorMessage={update.error?.message}
				onChange={update.reset}
				onCancel={editing.close}
				onSave={(value) => {
					update.mutate({ observations: value }, { onSuccess: editing.close })
				}}
			/>
		</div>
	)
}

function useMarkNotificationRead() {
	const [searchParams] = useSearchParams()
	const id = searchParams.get("notificationId")
	const { mutate } = useReadNotification()
	const { notify } = useNotification()
	const markedNotificationIdRef = useRef<string | null>(null)

	useEffect(() => {
		if (id && markedNotificationIdRef.current !== id) {
			markedNotificationIdRef.current = id
			mutate(
				{ id },
				{
					onError(error) {
						markedNotificationIdRef.current = null
						notify.error({ title: error.message })
					},
				},
			)
		}
	}, [id, mutate, notify])
}
